import { createContext, useContext } from 'react';

/**
 * Everything behind the background-jobs provider except the provider itself:
 * the job model, the reducer, the scheduler, and the hook consumers use.
 *
 * Split out from `background-jobs.context.tsx` on purpose. A file that exports a
 * component alongside plain values cannot keep component state across a Fast
 * Refresh, and the scheduler in particular must live outside a component body —
 * it recurses (a finishing job pumps the queue again), and the React Compiler
 * cannot memoize a component that contains hoisted self-referencing functions.
 */

/**
 * How many jobs may run at once. The rest wait their turn.
 *
 * Each in-flight conversion holds its whole workbook in the tab's heap and polls
 * the server every few hundred milliseconds, so an unbounded fan-out is a memory
 * and request-rate problem rather than a speed win. Three keeps a handful of
 * files feeling parallel without either cost growing without limit.
 */
export const MAX_CONCURRENT_JOBS = 3;

export type BackgroundJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

/** Something offered on a finished job, e.g. "View pricelist". */
export interface BackgroundJobAction {
  /** Already translated — the panel renders it verbatim. */
  label: string;
  run: () => void;
}

/**
 * A job's current status line and bar fraction.
 *
 * `percent` is absent whenever there is no honest fraction to show, which the
 * panel renders as an indeterminate bar rather than inventing a number.
 */
export interface BackgroundJobProgress {
  /** Already translated — the panel renders it verbatim. */
  label: string;
  /** 0–100. Absent means indeterminate. */
  percent?: number;
}

/** What a job's `run` resolves to, describing how it finished. */
export interface BackgroundJobOutcome {
  /** Already translated. Replaces the progress label, e.g. "Downloaded x.xlsx". */
  label: string;
  action?: BackgroundJobAction;
}

/** Handed to a job's `run` so it can report as it goes. */
export interface BackgroundJobHandle {
  setProgress: (progress: BackgroundJobProgress) => void;
}

export interface StartJobInput {
  /** The row's heading — in practice the filename. */
  title: string;
  /** Status line to show once the job starts, before it reports anything. */
  initialLabel: string;
  run: (handle: BackgroundJobHandle) => Promise<BackgroundJobOutcome>;
  /**
   * Whether a failed job may be re-run from the panel. Opt-in rather than the
   * default: `run` re-executes verbatim, which is only safe when the work is
   * repeatable. An upload that failed *after* the server committed would
   * duplicate on retry, so those pass false and the user retries deliberately.
   */
  retryable?: boolean;
}

export interface BackgroundJob {
  id: string;
  title: string;
  status: BackgroundJobStatus;
  /** Current status line, already translated by whoever set it. */
  label: string;
  /** 0–100 while running and a real fraction is known. Absent = indeterminate. */
  percent?: number;
  /** Present once succeeded, when the job offered one. */
  action?: BackgroundJobAction;
  /** The error's message, when it had one. Absent on the other statuses. */
  errorMessage?: string;
  retryable: boolean;
  /**
   * Internal to the provider: held so `retryJob` can re-run the work without the
   * panel needing to know what the job does. Not for consumers to read.
   */
  descriptor: StartJobInput;
}

export interface BackgroundJobsContextData {
  /** Newest first, which is the order the panel renders. */
  jobs: BackgroundJob[];
  /**
   * Queues the work and returns the new job's id. It starts immediately when
   * fewer than {@link MAX_CONCURRENT_JOBS} are already running, and otherwise
   * waits — so callers must not assume the request is in flight on return.
   */
  startJob: (input: StartJobInput) => string;
  /** Ignored unless the job failed, is retryable, and is not already scheduled. */
  retryJob: (id: string) => void;
  /** Ignored for a running job: hiding the row would not stop the work. */
  dismissJob: (id: string) => void;
  /** Clears every succeeded and failed job, leaving queued and running ones. */
  dismissFinishedJobs: () => void;
}

export type JobEvent =
  | { type: 'enqueued'; job: BackgroundJob }
  | { type: 'started'; id: string }
  | { type: 'progress'; id: string; progress: BackgroundJobProgress }
  | { type: 'succeeded'; id: string; outcome: BackgroundJobOutcome }
  | { type: 'failed'; id: string; errorMessage?: string }
  | { type: 'requeued'; id: string }
  | { type: 'dismissed'; id: string }
  | { type: 'finishedDismissed' };

export const BackgroundJobsContext = createContext<
  BackgroundJobsContextData | undefined
>(undefined);

/** Stable identity, so the reducer's initial value doesn't change per render. */
export const NO_JOBS: BackgroundJob[] = [];

/** Strictly in flight — the only state with progress to show. */
export function isJobRunning(job: BackgroundJob): boolean {
  return job.status === 'running';
}

/** Not finished: either in flight or waiting for a slot. */
export function isJobActive(job: BackgroundJob): boolean {
  return job.status === 'running' || job.status === 'queued';
}

/** Replace one job, leaving the rest (and the order) untouched. */
function patchJob({
  jobs,
  id,
  patch,
}: {
  jobs: BackgroundJob[];
  id: string;
  patch: Partial<BackgroundJob>;
}): BackgroundJob[] {
  return jobs.map((job) => (job.id === id ? { ...job, ...patch } : job));
}

export function jobsReducer(
  jobs: BackgroundJob[],
  event: JobEvent,
): BackgroundJob[] {
  switch (event.type) {
    case 'enqueued':
      return [event.job, ...jobs];
    case 'started':
      return patchJob({ jobs, id: event.id, patch: { status: 'running' } });
    case 'progress':
      // Only while running: a late reading from a job that already finished
      // would otherwise overwrite its outcome label.
      //
      // `percent` is assigned rather than spread, because spreading a progress
      // that omits it would leave the previous value in place — so a job moving
      // from a measured phase to an unmeasurable one (upload consumed, rows
      // still converting) would keep drawing a determinate bar at its last
      // fraction instead of going indeterminate.
      return jobs.map((job) =>
        job.id === event.id && isJobRunning(job)
          ? {
              ...job,
              label: event.progress.label,
              percent: event.progress.percent,
            }
          : job,
      );
    case 'succeeded':
      return patchJob({
        jobs,
        id: event.id,
        patch: {
          status: 'succeeded',
          label: event.outcome.label,
          action: event.outcome.action,
          percent: undefined,
          errorMessage: undefined,
        },
      });
    case 'failed':
      return patchJob({
        jobs,
        id: event.id,
        patch: {
          status: 'failed',
          errorMessage: event.errorMessage,
          percent: undefined,
          action: undefined,
        },
      });
    case 'requeued':
      return patchJob({
        jobs,
        id: event.id,
        patch: {
          status: 'queued',
          label:
            jobs.find((job) => job.id === event.id)?.descriptor.initialLabel ??
            '',
          percent: undefined,
          errorMessage: undefined,
          action: undefined,
        },
      });
    case 'dismissed':
      return jobs.filter((job) => job.id !== event.id);
    case 'finishedDismissed':
      return jobs.filter(isJobActive);
  }
}

function toQueuedJob({
  id,
  input,
}: {
  id: string;
  input: StartJobInput;
}): BackgroundJob {
  return {
    id,
    title: input.title,
    status: 'queued',
    label: input.initialLabel,
    retryable: input.retryable ?? false,
    descriptor: input,
  };
}

/**
 * Run one job to completion, reporting through `dispatch`.
 *
 * Deliberately never rejects: a job's failure is a row in the panel, not an
 * unhandled rejection, and the scheduler relies on it always settling to release
 * the slot it took.
 */
async function runJob({
  id,
  input,
  dispatch,
}: {
  id: string;
  input: StartJobInput;
  dispatch: (event: JobEvent) => void;
}): Promise<void> {
  try {
    const outcome = await input.run({
      setProgress: (progress) => dispatch({ type: 'progress', id, progress }),
    });
    dispatch({ type: 'succeeded', id, outcome });
  } catch (error) {
    dispatch({
      type: 'failed',
      id,
      errorMessage: error instanceof Error ? error.message : undefined,
    });
  }
}

export interface JobScheduler {
  /** Queue new work and return its id. */
  start: (input: StartJobInput) => string;
  /** Re-run a job the caller has read from state. Checks its own invariants. */
  retry: (job: BackgroundJob) => void;
  /** Cancel a queued job. Ignores one that is already running. */
  dismiss: (id: string) => void;
}

/**
 * The queue, and the only thing that starts work.
 *
 * Lives outside React for two reasons. Scheduling has to know how many jobs are
 * running *at the instant* it decides to start one, and reducer state is the last
 * render's snapshot — two jobs queued in the same tick would both read zero
 * running and both start, defeating the cap. And it recurses, since a finishing
 * job pumps the queue again; a self-referencing hoisted function inside a
 * component body is exactly what stops the React Compiler memoizing it.
 *
 * `dispatch` from `useReducer` is stable for the life of the provider, so one
 * scheduler is created per provider and never replaced.
 */
export function createJobScheduler(
  dispatch: (event: JobEvent) => void,
): JobScheduler {
  const waiting: { id: string; input: StartJobInput }[] = [];
  const running = new Set<string>();

  /** In flight or already waiting for a slot — the live view of "scheduled". */
  function isScheduled(id: string): boolean {
    return running.has(id) || waiting.some((entry) => entry.id === id);
  }

  /** Start as many waiting jobs as there is capacity for, oldest first. */
  function pump(): void {
    while (running.size < MAX_CONCURRENT_JOBS) {
      const next = waiting.shift();
      if (next === undefined) return;
      running.add(next.id);
      dispatch({ type: 'started', id: next.id });
      // `runJob` never rejects, so this always releases the slot.
      void runJob({ id: next.id, input: next.input, dispatch }).then(() => {
        running.delete(next.id);
        pump();
      });
    }
  }

  return {
    start(input) {
      const id = crypto.randomUUID();
      dispatch({ type: 'enqueued', job: toQueuedJob({ id, input }) });
      waiting.push({ id, input });
      pump();
      return id;
    },

    retry(job) {
      // `retryable` is a claim about whether re-running the work is safe, so it
      // is enforced here — on the path that actually re-runs it — and not left to
      // the panel that happens to render the button.
      if (!job.retryable || job.status !== 'failed') return;
      // The caller read `job` from the last render, which would let two clicks in
      // one tick both see a failed job and schedule it twice.
      if (isScheduled(job.id)) return;
      // Back through the queue rather than straight to running, so a retry cannot
      // push past the concurrency limit.
      dispatch({ type: 'requeued', id: job.id });
      waiting.push({ id: job.id, input: job.descriptor });
      pump();
    },

    dismiss(id) {
      // Asked of the queue, not of rendered state: a job that started since the
      // last render still reads as queued there, and dismissing a running row
      // would hide work that carries on regardless.
      if (running.has(id)) return;
      // A queued job has not started, so dismissing it genuinely cancels it —
      // drop it from the queue as well as from the panel.
      const index = waiting.findIndex((entry) => entry.id === id);
      if (index !== -1) waiting.splice(index, 1);
      dispatch({ type: 'dismissed', id });
    },
  };
}

export function useBackgroundJobs(): BackgroundJobsContextData {
  const context = useContext(BackgroundJobsContext);
  if (context === undefined) {
    throw new Error(
      'useBackgroundJobs must be used within a BackgroundJobsProvider',
    );
  }
  return context;
}
